/**
 * List of the signed-in user's own assessments — title, mode, status, updated_at.
 * Routes to /summary/:id for completed work, or resumes /assessment/:id otherwise.
 */
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, FileText, ListTodo } from "lucide-react";
import { saveResume } from "@/lib/api";

const MODE_LABEL = { solopreneur: "Solopreneur", sme: "SME", corporate: "Corporate Department" };

export default function AssessmentsList({ rows, loading }) {
  const navigate = useNavigate();

  return (
    <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      {loading ? (
        <p className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-slate-600 dark:text-slate-300">No assessments yet.</p>
          <Link
            to="/mode"
            className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
          >
            Start a new assessment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {rows.map((r) => (
            <li key={r.id} data-testid={`account-row-${r.id}`} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="font-display text-base text-slate-900 dark:text-slate-50 truncate">
                  {r.organization || "(no organisation set)"}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {MODE_LABEL[r.mode] || r.mode} · {r.status === "completed" ? "Completed" : "In progress"} · updated{" "}
                  {new Date(r.updated_at).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === "completed" ? (
                  <>
                    {r.paid_managed && (
                      <Link
                        to={`/summary/${r.id}?tab=action-plan`}
                        data-testid={`account-open-plan-${r.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-500 transition-colors"
                      >
                        <ListTodo className="h-4 w-4" /> Open Plan
                      </Link>
                    )}
                    <Link
                      to={`/summary/${r.id}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors"
                    >
                      <FileText className="h-4 w-4" /> View
                    </Link>
                  </>
                ) : (
                  <button
                    onClick={() => { saveResume({ id: r.id, mode: r.mode, status: r.status }); navigate(`/assessment/${r.id}`); }}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  >
                    Resume <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
